'use client'

/**
 * Per-user Country and Industry view (docs/tax-addition/02-build-contract.md §9-§10).
 *
 * The selection is stored on the server (`PUT /api/me/preferences`, keys
 * `ap.country` / `ap.industry`) and the backend applies it to every scoped
 * endpoint on top of the owner scope. This provider:
 *  - loads the stored view and the switcher options (`GET /api/me/view`),
 *  - mirrors the view into the `X-AP-Country` / `X-AP-Industry` request headers
 *    (headers can only narrow on the server, never widen),
 *  - sets the money locale for a single-country view (en-IN lakh grouping, ...),
 *  - remounts the page (`ViewScope`) after a change so every query refetches.
 *
 * The view is changed from the "My view" card (Settings, Admin Settings); the
 * header only shows a read-only chip when it is not All. A country may be a
 * region (Europe, `EU`, `kind: 'region'`): every European invoice, totals in EUR.
 *
 * The last view is cached in localStorage per signed-in user so a reload applies
 * it as soon as the session is known, before the server answers; the server copy
 * always wins. The first render always uses the ALL defaults, the same as the
 * server render, so hydration never mismatches: the cache is read through
 * `useSyncExternalStore`, whose server snapshot is empty.
 */

import React from 'react'
import { useSession } from 'next-auth/react'
import { apiClient, setViewHeaders, VIEW_HEADER_COUNTRY, VIEW_HEADER_INDUSTRY } from '@/lib/api-client'
import { DEFAULT_LOCALE, localeForCountry, setDisplayLocale } from '@/lib/display-locale'
import { ALL, isRegion, sameView } from '@/lib/view-labels'

export { ALL }
const CACHE_PREFIX = 'ap.view.v1'

export interface ViewSelection {
  country: string
  industry: string
}

export interface CountryOption {
  code: string
  name: string | null
  currency: string | null
  locale: string | null
  has_pack: boolean
  has_invoices: boolean
  /** `region` for Europe (`EU`): the pack's member countries are in `members`. */
  kind?: 'country' | 'region'
  members?: string[]
}

export interface IndustryOption {
  code: string
  label: string
}

interface ViewResponse {
  stored: ViewSelection
  active: ViewSelection
  countries: CountryOption[]
  industries: IndustryOption[]
  reporting_currency: string
}

type FocusTarget = 'country' | 'industry' | 'save' | null

interface CountryContextValue {
  view: ViewSelection
  countries: CountryOption[]
  industries: IndustryOption[]
  /** Currency money is reported in: the country's own for one country, else the reporting currency. */
  displayCurrency: string | null
  reportingCurrency: string | null
  locale: string
  loading: boolean
  saving: boolean
  error: string | null
  /** Changes whenever the view changes; `ViewScope` remounts on it. */
  viewKey: string
  setView: (next: Partial<ViewSelection>, focus?: FocusTarget) => Promise<void>
  countryLabel: (code: string) => string
  industryLabel: (code: string) => string
  /** The control to refocus after the page remounts (keyboard users keep their place). */
  takeFocusTarget: () => FocusTarget
}

const ALL_VIEW: ViewSelection = { country: ALL, industry: ALL }

const CountryContext = React.createContext<CountryContextValue | null>(null)

/** One cache entry per user (Keycloak `sub`), so a shared browser never shows another user's view. */
function cacheKey(userId: string): string {
  return `${CACHE_PREFIX}:${userId}`
}

interface CachedView {
  view: ViewSelection
  locale: string
}

function readCacheRaw(userId: string): string | null {
  try {
    return window.localStorage.getItem(cacheKey(userId))
  } catch {
    return null
  }
}

function parseCache(raw: string | null): CachedView | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { view?: ViewSelection; locale?: string }
    if (!parsed.view?.country || !parsed.view?.industry) return null
    return { view: { country: parsed.view.country, industry: parsed.view.industry }, locale: parsed.locale || DEFAULT_LOCALE }
  } catch {
    return null
  }
}

/** Another tab changing the cache re-renders this one; the server copy still wins. */
function subscribeStorage(onChange: () => void): () => void {
  window.addEventListener('storage', onChange)
  return () => window.removeEventListener('storage', onChange)
}

function writeCache(userId: string | null, view: ViewSelection, locale: string): void {
  if (!userId) return
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify({ view, locale }))
  } catch {
    // Private window or blocked storage: the server copy still holds the view.
  }
}

let regionNames: Intl.DisplayNames | null = null
function regionName(code: string): string | null {
  try {
    regionNames ??= new Intl.DisplayNames(['en'], { type: 'region' })
    const name = regionNames.of(code)
    return name && name !== code ? name : null
  } catch {
    return null
  }
}

/** `GET /api/me/view` with explicit view headers (the request carries exactly this view). */
function fetchView(headers: ViewSelection): Promise<ViewResponse> {
  return apiClient.get<ViewResponse>('/api/me/view', {
    headers: { [VIEW_HEADER_COUNTRY]: headers.country, [VIEW_HEADER_INDUSTRY]: headers.industry },
  })
}

function savePreference(view: ViewSelection): Promise<unknown> {
  return apiClient.put('/api/me/preferences', {
    preferences: { 'ap.country': view.country, 'ap.industry': view.industry },
  })
}

function localeFor(view: ViewSelection, countries: CountryOption[]): string {
  if (view.country === ALL) return DEFAULT_LOCALE
  const option = countries.find((c) => c.code === view.country)
  return localeForCountry(view.country, option?.locale)
}

export function CountryProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()
  // Fall back to the email so a session without `sub` still records view changes;
  // without a user id the selector saved the preference but never re-rendered.
  const userId = status === 'authenticated' ? (session?.sub ?? session?.user?.email ?? null) : null
  /** The view the server confirmed for this user; until then the cached view, then ALL. */
  const [confirmed, setConfirmed] = React.useState<(CachedView & { user: string }) | null>(null)
  const [countries, setCountries] = React.useState<CountryOption[]>([])
  const [industries, setIndustries] = React.useState<IndustryOption[]>([])
  const [reportingCurrency, setReportingCurrency] = React.useState<string | null>(null)
  const [loaded, setLoaded] = React.useState(false)
  const loading = status === 'loading' || (status === 'authenticated' && !loaded)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  /** The view the server holds as the user's preference (what a failed change reverts to). */
  const savedView = React.useRef<ViewSelection>(ALL_VIEW)
  const focusTarget = React.useRef<FocusTarget>(null)

  const cachedRaw = React.useSyncExternalStore(
    subscribeStorage,
    () => (userId ? readCacheRaw(userId) : null),
    () => null
  )
  const cached = React.useMemo(() => parseCache(cachedRaw), [cachedRaw])
  const current: CachedView =
    (confirmed && confirmed.user === userId ? confirmed : null) ?? cached ?? { view: ALL_VIEW, locale: DEFAULT_LOCALE }
  const view = current.view

  // Mirror the view into the module-level request headers and money locale before the
  // children render (both are plain idempotent assignments, not React state).
  setViewHeaders(view)
  setDisplayLocale(current.locale)

  const apply = React.useCallback((next: ViewSelection, options: CountryOption[]) => {
    const locale = localeFor(next, options)
    writeCache(userId, next, locale)
    if (userId) setConfirmed({ user: userId, view: next, locale })
  }, [userId])

  const adopt = React.useCallback((res: ViewResponse, view: ViewSelection) => {
    setCountries(res.countries ?? [])
    setIndustries(res.industries ?? [])
    setReportingCurrency(res.reporting_currency ?? null)
    apply(view, res.countries ?? [])
  }, [apply])

  React.useEffect(() => {
    if (status !== 'authenticated') return
    let cancelled = false
    // Ask without narrowing headers: the stored preference is the source of truth.
    fetchView(ALL_VIEW)
      .then((res) => {
        if (cancelled) return
        savedView.current = res.stored
        adopt(res, res.stored)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load your view.')
      })
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [status, adopt])

  const setView = React.useCallback(async (next: Partial<ViewSelection>, focus: FocusTarget = null) => {
    const target: ViewSelection = { ...view, ...next }
    if (sameView(target, view)) return
    const previous = savedView.current
    setSaving(true)
    setError(null)
    let stored = false
    try {
      await savePreference(target)
      stored = true
      savedView.current = target
      const res = await fetchView(target)
      focusTarget.current = focus
      adopt(res, res.active)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save your view.')
      // The preference was saved but the view could not be loaded: put the saved
      // preference back so the server and the page agree on the view shown.
      if (stored) {
        try {
          await savePreference(previous)
          savedView.current = previous
        } catch {
          // Could not revert either: show what the server now holds instead.
          apply(target, countries)
        }
      }
    } finally {
      setSaving(false)
    }
  }, [view, adopt, apply, countries])

  const countryLabel = React.useCallback((code: string) => {
    if (code === ALL) return 'All countries'
    const option = countries.find((c) => c.code === code)
    // A region is named by its pack ("Europe"); Intl would call EU "European Union", which excludes GB and CH.
    if (isRegion(option)) return option?.name ?? code
    return regionName(code) ?? option?.name ?? code
  }, [countries])

  const industryLabel = React.useCallback((code: string) => {
    if (code === ALL) return 'All industries'
    return industries.find((i) => i.code === code)?.label ?? code
  }, [industries])

  const takeFocusTarget = React.useCallback(() => {
    const target = focusTarget.current
    focusTarget.current = null
    return target
  }, [])

  const value = React.useMemo<CountryContextValue>(() => {
    const option = countries.find((c) => c.code === view.country)
    return {
      view,
      countries,
      industries,
      displayCurrency: view.country === ALL ? reportingCurrency : (option?.currency ?? reportingCurrency),
      reportingCurrency,
      locale: current.locale,
      loading,
      saving,
      error,
      viewKey: `${view.country}|${view.industry}`,
      setView,
      countryLabel,
      industryLabel,
      takeFocusTarget,
    }
  }, [view, current.locale, countries, industries, reportingCurrency, loading, saving, error, setView,
    countryLabel, industryLabel, takeFocusTarget])

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
}

/** Remounts its children when the view changes, so every page refetches its data. */
export function ViewScope({ children }: { children: React.ReactNode }) {
  const ctx = React.useContext(CountryContext)
  return <React.Fragment key={ctx?.viewKey ?? 'all'}>{children}</React.Fragment>
}

export function useCountry(): CountryContextValue {
  const ctx = React.useContext(CountryContext)
  if (!ctx) throw new Error('useCountry must be used inside CountryProvider')
  return ctx
}
